import pluralize from 'pluralize'
import { useState } from 'react'
import toast from 'react-hot-toast'

import { FvButton } from '@fv/client-components'
import type { QuoteReasonPayload } from '@fv/models'

import {
  ShipperSlider,
  type ShipperSliderProps,
} from '@/components/ShipperSlider'
import { useUpdateMultipleQuoteReasons } from '@/features/quote-reason/hooks'

import AwardCarrierRow, { type AwardCarrierLoad } from './AwardCarrierRow'
import InfoBox from './InfoBox'
import { PageHeader } from './PageHeader'

type TruckloadAwardConfirmationProps = Pick<
  ShipperSliderProps,
  'onClose' | 'isOpen'
> & {
  loadsToBook: AwardCarrierLoad[]
  showUnawarded?: boolean
  isBookingPage?: boolean
  submitLabel?: string
  onSubmit: () => void
  onCancel?: () => void
  cancelText?: string
  title?: string
}
export const TruckloadAwardConfirmation = ({
  onClose,
  isOpen,
  loadsToBook,
  isBookingPage,
  showUnawarded,
  submitLabel,
  onSubmit,
  onCancel,
  cancelText,
  title = 'Award carrier confirmation',
}: TruckloadAwardConfirmationProps) => {
  const [quoteReasons, setQuoteReasons] = useState(
    Object.fromEntries(
      loadsToBook.map<[string, QuoteReasonPayload | undefined]>(load => [
        load.loadId,
        undefined,
      ]),
    ),
  )
  const quoteReasonMutation = useUpdateMultipleQuoteReasons()
  const loads = loadsToBook.filter(load => showUnawarded || !!load.quoteId)
  const allTrucksAwarded = !loads.some(l => !l.quoteId)
  const showIncompleteConfirmation = !allTrucksAwarded && !isBookingPage
  const defaultBookingLabel = allTrucksAwarded
    ? `Book ${pluralize('this', loadsToBook.length)} ${pluralize(
        'truck',
        loadsToBook.length,
      )}`
    : `Continue booking selected ${pluralize('truck', loadsToBook.length)}`

  const handleSubmit = async () => {
    const definedReasons = Object.fromEntries(
      Object.entries(quoteReasons).filter(([_, reason]) => !!reason),
    )
    if (isBookingPage && Object.keys(definedReasons).length) {
      try {
        await quoteReasonMutation.mutateAsync(definedReasons)
        onSubmit()
      } catch {
        toast.error('There was an error saving your selection')
      }
    } else {
      onSubmit()
    }
  }

  return (
    <ShipperSlider onClose={onClose} isOpen={isOpen}>
      <div>
        <PageHeader title={title} />
        {loads.map((load, i) => (
          <AwardCarrierRow
            key={load.loadId}
            load={load}
            quoteId={load.quoteId}
            truckNumber={i + 1}
            quoteReason={quoteReasons[load.loadId]}
            {...(isBookingPage && {
              onQuoteReasonChange: reason =>
                setQuoteReasons({
                  ...quoteReasons,
                  [load.loadId]: reason,
                }),
            })}
          />
        ))}

        {showIncompleteConfirmation && (
          <InfoBox className="mb-6">
            This is a multi-truck shipment. Would you like to wait to book this
            shipment until all trucks are chosen? If not, you may proceed to the
            booking details for the chosen trucks. You can come back here and
            fill remaining trucks at any time.
          </InfoBox>
        )}

        <div className="flex items-center justify-end">
          {showIncompleteConfirmation && (
            <FvButton theme="default" onClick={onClose} icon="ban">
              <span>Wait until I select all trucks</span>
            </FvButton>
          )}

          {onCancel && cancelText && (
            <FvButton theme="plain" icon="times" onClick={onCancel}>
              {cancelText}
            </FvButton>
          )}

          <FvButton
            theme="primary"
            fwd
            loading={quoteReasonMutation.isPending}
            icon="check"
            onClick={handleSubmit}
          >
            {submitLabel ?? defaultBookingLabel}
          </FvButton>
        </div>
      </div>
    </ShipperSlider>
  )
}
